import {useDirection} from '@base-ui/react/direction-provider';
import {formatDateLocalized, isoToDate} from '@sollapay/utils';
import {format} from 'date-fns';
import {CalendarDays} from 'lucide-react';
import {useId, useMemo, useState} from 'react';
import {he} from 'react-day-picker/locale';

import {useSkeletonReveal} from '../../hooks';
import {cn} from '../../lib/utils';
import {Field, FieldSkeleton} from '../Field';
import {Calendar} from '../shadcn/calendar';
import {Popover, PopoverContent, PopoverTrigger} from '../shadcn/popover';

import type {FieldProps} from '../Field';
import type {ReactNode} from 'react';

export interface FieldDatePickerProps {
  label?: ReactNode;
  description?: ReactNode;
  error?: string;
  orientation?: FieldProps['orientation'];
  id?: string;
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  className?: string;
  startAdornment?: ReactNode;
  badge?: ReactNode;
  /** Corner badge rendered opposite the label — e.g. an AI generating/filled indicator. */
  cornerBadge?: ReactNode;
  /** Shows a pulsing skeleton in place of the value while it's being loaded/generated. */
  isLoading?: boolean;
  formatDate?: (date: Date) => string;
  monthOnly?: boolean;
  /** Renders a lock glyph next to the label and prevents opening the calendar — for a permanently non-editable field. */
  isLocked?: boolean;
  /** Tooltip shown on the lock glyph explaining why the field can't be edited. */
  lockedTooltip?: ReactNode;
  /** Disables every day after today in the calendar — for fields like date of birth or a registration date that can never be in the future. Has no effect with `monthOnly`, which never shows a day grid to disable. */
  disableFuture?: boolean;
}

export function FieldDatePicker({
  label,
  description,
  error,
  orientation,
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  readOnly,
  required,
  className,
  startAdornment,
  badge,
  cornerBadge,
  isLoading,
  formatDate,
  monthOnly = false,
  isLocked,
  lockedTooltip,
  disableFuture
}: FieldDatePickerProps) {
  const dir = useDirection();
  const isRtl = dir === 'rtl';

  const generatedId = useId();
  const resolvedId = id ?? generatedId;
  const hasError = !!error;
  const selectedDate = useMemo(() => (value ? isoToDate(value) : undefined), [value]);
  const [open, setOpen] = useState(false);
  const {showSkeleton, onFocus: onRevealFocus} = useSkeletonReveal(isLoading);

  const effectiveAdornment = startAdornment ?? <CalendarDays className="size-4" />;

  const displayValue = selectedDate
    ? formatDate
      ? formatDate(selectedDate)
      : monthOnly
        ? format(selectedDate, 'MM/yyyy')
        : formatDateLocalized(selectedDate, isRtl ? 'he' : 'en')
    : (placeholder ?? '');

  const handleSelect = (date?: Date) => {
    onChange?.(date ? format(date, 'yyyy-MM-dd') : '');
    setOpen(false);
    onBlur?.();
  };

  const handleMonthChange = (month: Date) => {
    onChange?.(format(month, 'yyyy-MM'));
  };

  return (
    <Field
      label={label}
      description={description}
      error={error}
      htmlFor={resolvedId}
      orientation={orientation}
      required={required}
      cornerBadge={cornerBadge}
      isLocked={isLocked}
      lockedTooltip={lockedTooltip}
    >
      <Popover open={readOnly ? false : open} onOpenChange={readOnly ? () => {} : setOpen}>
        <PopoverTrigger
          id={resolvedId}
          name={name}
          aria-invalid={hasError || undefined}
          disabled={disabled || isLocked}
          onFocus={onRevealFocus}
          className={cn(
            'input-base field-focus field-error field-disabled relative inline-flex h-auto items-center justify-between ps-9 text-start text-sm font-normal',
            !selectedDate && 'text-fg-disabled',
            badge && 'pe-14',
            className
          )}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-s-3 top-1/2 -translate-y-1/2 text-fg-tertiary"
          >
            {effectiveAdornment}
          </span>

          <span className={cn('w-full truncate text-start', showSkeleton && 'invisible')}>
            {displayValue}
          </span>

          {showSkeleton && <FieldSkeleton className="inset-s-9 top-1/2 -translate-y-1/2" />}

          {badge && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-e-2 top-1/2 -translate-y-1/2"
            >
              {badge}
            </span>
          )}
        </PopoverTrigger>

        <PopoverContent className="w-auto p-3" align="start">
          {monthOnly ? (
            <Calendar
              mode="single"
              captionLayout="dropdown"
              month={selectedDate}
              onMonthChange={handleMonthChange}
              dir={isRtl ? 'rtl' : 'ltr'}
              locale={isRtl ? he : undefined}
              className="p-0"
              classNames={{
                nav: 'hidden',
                month_grid: 'hidden',
                weekdays: 'hidden'
              }}
            />
          ) : (
            <Calendar
              mode="single"
              captionLayout="dropdown"
              selected={selectedDate}
              onSelect={handleSelect}
              disabled={disableFuture ? {after: new Date()} : undefined}
              showOutsideDays
              buttonVariant="tertiary"
              dir={isRtl ? 'rtl' : 'ltr'}
              locale={isRtl ? he : undefined}
              className="[--cell-size:--spacing(10)] p-0"
              classNames={{
                weekday:
                  'w-(--cell-size) rounded-full text-calendar-weekday font-normal text-fg-tertiary'
              }}
            />
          )}
        </PopoverContent>
      </Popover>
    </Field>
  );
}
